import { FC, useRef, useState, useEffect, useCallback } from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Fab from '@material-ui/core/Fab';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import CloseIcon from '@material-ui/icons/Close';

import COLORS from 'constants/colors';
import { compressImage } from 'components/fileuploader/ImageUploader2';

// Limit camera images to 10 to prevent excessive memory usage, maintain good UX, and reduce AI processing costs
const MAX_CAMERA_IMAGES = 10;

// Camera timing constants
const CAMERA_READY_TIMEOUT_MS = 2000; // Fallback timeout if video metadata doesn't load
const FLASH_ANIMATION_DURATION_MS = 200; // Duration of flash effect when capturing photo
const IMAGE_MAX_SIZE_MB = 25; // Maximum file size in MB before compression (higher = better quality)

// Style constants - extracted to prevent recreation on each render
const MODAL_CONTAINER_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#000',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
};

const FLASH_OVERLAY_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#fff',
  zIndex: 10001,
  animation: 'cameraFlash 0.2s ease-out',
  pointerEvents: 'none',
};

const CLOSE_BUTTON_CONTAINER_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 16,
  right: 16,
  zIndex: 10000,
};

const CLOSE_BUTTON_STYLE: React.CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.9)',
};

const COUNTER_CONTAINER_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 16,
  left: 16,
  zIndex: 10000,
  color: '#fff',
  padding: '8px 16px',
  borderRadius: 20,
  fontSize: '16px',
  fontWeight: 'bold',
};

const VIDEO_CONTAINER_STYLE: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  position: 'relative',
};

const VIDEO_STYLE: React.CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const LOADING_OVERLAY_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  color: '#fff',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
};

const CONTROLS_CONTAINER_STYLE: React.CSSProperties = {
  padding: '20px',
  backgroundColor: 'rgba(0,0,0,0.8)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
};

const COMPLETE_BUTTON_STYLE: React.CSSProperties = {
  position: 'absolute',
  right: '20px',
  backgroundColor: '#fff',
  color: '#000',
  padding: '12px 24px',
};

const CANVAS_STYLE: React.CSSProperties = {
  display: 'none',
};

interface CameraCaptureProps {
  /** Whether camera modal is open */
  isOpen: boolean;
  /** Callback when photos are captured and "Klar" is clicked */
  onPhotosCapture: (photos: File[]) => void;
  /** Callback when camera is cancelled/closed without saving */
  onCancel: () => void;
}

/**
 * CameraCapture Component
 * 
 * Provides a full-screen camera interface for capturing product photos on mobile devices.
 * 
 * Features:
 * - Uses MediaDevices API for camera access (requires HTTPS)
 * - Continuous photo capture (up to 10 photos)
 * - Image compression to reduce file size
 * - Flash animation on capture
 * - Photo counter with visual feedback at limit
 * - Permission error handling with user-friendly dialog
 * - Automatic stream cleanup on unmount
 * 
 * Browser Compatibility:
 * - iOS Safari 14.3+ ✅
 * - Android Chrome ✅
 * - iOS Safari <14.3 ❌ (MediaDevices API not supported)
 * 
 * @example
 * ```tsx
 * <CameraCapture
 *   isOpen={isCameraOpen}
 *   onPhotosCapture={(photos) => setFiles(prev => [...prev, ...photos])}
 *   onCancel={() => setIsCameraOpen(false)}
 * />
 * ```
 */
const CameraCapture: FC<CameraCaptureProps> = ({ isOpen, onPhotosCapture, onCancel }) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedPhotos, setCapturedPhotos] = useState<File[]>([]);
  const [isFlashing, setIsFlashing] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [showPermissionError, setShowPermissionError] = useState(false);
  const [isOpeningCamera, setIsOpeningCamera] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraReadyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const flashTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null); // Ref to avoid stale closure in cleanup
  const isMountedRef = useRef(true); // Track mount state to prevent state updates after unmount

  /**
   * Cleanup camera stream and clear all timeouts
   */
  const cleanupCameraStream = () => {
    // Use ref to get current stream value (avoids stale closure)
    const currentStream = streamRef.current;
    if (currentStream) {
      currentStream.getTracks().forEach(track => {
        track.onended = null; // Clear handler to avoid stale closure
        track.stop();
      });
      streamRef.current = null;
      setStream(null);
    }
    // Clear video srcObject to release decoder resources
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    // Clear camera ready timeout if it's still pending
    if (cameraReadyTimeoutRef.current) {
      clearTimeout(cameraReadyTimeoutRef.current);
      cameraReadyTimeoutRef.current = null;
    }
    // Clear flash timeout if it's still pending
    if (flashTimeoutRef.current) {
      clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }
    setIsCameraReady(false);
    setIsFlashing(false);
    setIsOpeningCamera(false);
  };

  /**
   * Initialize camera stream when modal opens
   */
  useEffect(() => {
    if (isOpen && !stream) {
      openCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen - we want to open camera when modal opens, not when stream changes

  /**
   * Cleanup camera stream on unmount
   * Calls cleanupCameraStream which handles all cleanup logic
   */
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cleanupCameraStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only cleanup on unmount

  /**
   * P3: Handle tab visibility changes
   * When user switches tabs/apps, cleanup camera to free resources
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && streamRef.current) {
        console.log('Tab hidden - cleaning up camera');
        cleanupCameraStream();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // streamRef avoids need for stream dependency

  /**
   * Request camera access and initialize video stream
   */
  const openCamera = useCallback(async () => {
    // P1: Prevent race condition - don't open camera if already opening
    if (isOpeningCamera) return;
    
    // P0: Check browser compatibility before attempting to access camera
    if (!navigator.mediaDevices?.getUserMedia) {
      console.error('Camera API not supported in this browser');
      setShowPermissionError(true);
      return;
    }
    
    setIsOpeningCamera(true);
    
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 4096 }, // Request high resolution width
          height: { ideal: 2160 }, // Request high resolution height
        },
        audio: false,
      });

      // P0: Attempt to enable continuous autofocus for closeups
      // This is the robust/optimal way: check capabilities then apply constraints to the track
      try {
        const track = mediaStream.getVideoTracks()[0];
        if (track) {
          // Check capabilities if available (modern browsers)
          const capabilities = (track as any).getCapabilities?.() || {};
          
          // If focusMode is supported and includes 'continuous', or if we can't check (try anyway safe-ish)
          // Note: We check for 'continuous' support to avoid OverconstrainedError
          if (!capabilities.focusMode || capabilities.focusMode.includes('continuous')) {
            await track.applyConstraints({
              advanced: [{ focusMode: 'continuous' }] as any
            });
          }
        }
      } catch (focusError) {
        // Non-blocking: Ignore focus errors as some devices don't support it
        console.debug('Autofocus not supported:', focusError);
      }
      
      // P0 CRITICAL: Check if component is still mounted and videoRef exists
      if (!isMountedRef.current || !videoRef.current) {
        console.error('Component unmounted or video element not available');
        // Cleanup stream since we can't use it
        mediaStream.getTracks().forEach(track => track.stop());
        if (isMountedRef.current) {
          setIsOpeningCamera(false);
        }
        return;
      }
      
      streamRef.current = mediaStream; // Keep ref in sync
      setStream(mediaStream);
      setIsCameraReady(false);
      setCapturedPhotos([]);
      
      // Set video stream to video element
      videoRef.current.srcObject = mediaStream;
      
      // P3: Add stream ended handler to detect when camera stops unexpectedly
      mediaStream.getTracks().forEach(track => {
        track.onended = () => {
          console.warn('Camera stream ended unexpectedly');
          cleanupCameraStream();
          if (isMountedRef.current) {
            setShowPermissionError(true);
          }
        };
      });
      
      // Fallback: Set ready after timeout if metadata doesn't load
      // (Safari sometimes doesn't fire onLoadedMetadata reliably)
      cameraReadyTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setIsCameraReady(true);
        }
        cameraReadyTimeoutRef.current = null;
      }, CAMERA_READY_TIMEOUT_MS);
      
      setIsOpeningCamera(false);
    } catch (error: unknown) {
      console.error('Error accessing camera:', error);
      if (isMountedRef.current) {
        setShowPermissionError(true);
        setIsOpeningCamera(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpeningCamera]); // Depend on isOpeningCamera (cleanupCameraStream is stable)

  /**
   * Cancel camera and discard captured photos
   */
  const handleCancel = () => {
    cleanupCameraStream();
    setCapturedPhotos([]);
    onCancel();
  };

  /**
   * Save captured photos and close camera
   */
  const handleComplete = () => {
    if (capturedPhotos.length > 0) {
      onPhotosCapture(capturedPhotos);
      setCapturedPhotos([]);
    }
    cleanupCameraStream();
    onCancel();
  };

  /**
   * Capture photo from video stream
   * - Draws current video frame to canvas
   * - Converts to blob and compresses
   * - Adds to capturedPhotos array
   */
  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current || isCapturing) return;

    // Check if max images reached
    if (capturedPhotos.length >= MAX_CAMERA_IMAGES) {
      return;
    }

    const video = videoRef.current;
    
    // Validate video is ready
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      console.warn('Video not ready yet');
      return;
    }

    setIsCapturing(true);
    
    // Trigger flash animation with tracked timeout
    setIsFlashing(true);
    flashTimeoutRef.current = setTimeout(() => {
      setIsFlashing(false);
      flashTimeoutRef.current = null;
    }, FLASH_ANIMATION_DURATION_MS);

    const canvas = canvasRef.current;
    
    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw current video frame to canvas
    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to blob and then to File
      canvas.toBlob(async (blob) => {
        try {
          if (blob) {
            const timestamp = Date.now();
            const fileName = `camera-photo-${timestamp}.jpg`;
            const file = new File([blob], fileName, { type: 'image/jpeg' });
            
            // Compress the image using configured max file size
            const compressedFile = await compressImage(file, IMAGE_MAX_SIZE_MB);
            if (compressedFile) {
              setCapturedPhotos(prev => [...prev, compressedFile]);
            } else {
              console.error('Image compression failed');
              alert('Kunde inte komprimera bilden. Försök igen.');
            }
          } else {
            console.error('Failed to capture photo: canvas.toBlob returned null');
            alert('Kunde inte ta bilden. Försök igen.');
          }
        } catch (error) {
          console.error('Error during photo capture:', error);
          alert('Ett oväntat fel uppstod vid fotografering.');
        } finally {
          setIsCapturing(false);
        }
      }, 'image/jpeg', 0.95);
    } else {
      setIsCapturing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Camera Modal */}
      {!showPermissionError && (
        <div style={MODAL_CONTAINER_STYLE}>
        {/* Flash overlay */}
        {isFlashing && (
          <div style={FLASH_OVERLAY_STYLE} />
        )}
        <style>{`
          @keyframes cameraFlash {
            0% { opacity: 0; }
            50% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}</style>

        {/* Close button */}
        <div style={CLOSE_BUTTON_CONTAINER_STYLE}>
          <IconButton
            onClick={handleCancel}
            style={CLOSE_BUTTON_STYLE}
            aria-label="Stäng kamera"
          >
            <CloseIcon />
          </IconButton>
        </div>

        {/* Photo counter */}
        {capturedPhotos.length > 0 && (
          <div
            style={{
              ...COUNTER_CONTAINER_STYLE,
              backgroundColor: capturedPhotos.length >= MAX_CAMERA_IMAGES ? 'rgba(255,0,0,0.8)' : 'rgba(0,0,0,0.7)',
            }}
          >
            {capturedPhotos.length}/{MAX_CAMERA_IMAGES} {capturedPhotos.length === 1 ? 'bild' : 'bilder'}
          </div>
        )}

        {/* Video preview */}
        <div style={VIDEO_CONTAINER_STYLE}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            onLoadedMetadata={() => {
              // Clear fallback timeout since metadata loaded successfully
              if (cameraReadyTimeoutRef.current) {
                clearTimeout(cameraReadyTimeoutRef.current);
                cameraReadyTimeoutRef.current = null;
              }
              setIsCameraReady(true);
            }}
            aria-label="Kameraförhandsgranskning"
            style={VIDEO_STYLE}
          />
          {!isCameraReady && (
            <div style={LOADING_OVERLAY_STYLE}>
              <CircularProgress size={40} style={{ color: '#fff' }} />
              <span>Startar kamera...</span>
            </div>
          )}
        </div>

        {/* Controls */}
        <div style={CONTROLS_CONTAINER_STYLE}>
          {/* Camera button - always centered */}
          <Fab
            color="primary"
            onClick={capturePhoto}
            disabled={!isCameraReady || isCapturing || capturedPhotos.length >= MAX_CAMERA_IMAGES}
            aria-label="Ta foto"
            style={{
              backgroundColor: !isCameraReady || isCapturing || capturedPhotos.length >= MAX_CAMERA_IMAGES ? undefined : COLORS.mainGreen,
            }}
          >
            <CameraAltIcon />
          </Fab>
          
          {/* Klar button - positioned to the right */}
          {capturedPhotos.length > 0 && (
            <Button
              variant="contained"
              onClick={handleComplete}
              style={COMPLETE_BUTTON_STYLE}
            >
              Klar ({capturedPhotos.length})
            </Button>
          )}
        </div>

        {/* Hidden canvas for capturing photos */}
        <canvas ref={canvasRef} style={CANVAS_STYLE} />
      </div>
      )}

      {/* Camera Permission Error Dialog */}
      <Dialog
        open={showPermissionError}
        onClose={() => {
          setShowPermissionError(false);
          onCancel();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Kamerabehörighet krävs</DialogTitle>
        <DialogContent>
          <p style={{ marginBottom: 16 }}>
            För att ta bilder behöver du ge denna webbplats tillgång till kameran.
          </p>
          <p style={{ marginBottom: 16, lineHeight: 1.6 }}>
            Gå till din enhets <strong>Inställningar</strong> och aktivera kamerabehörighet för din webbläsare, sedan försök igen.
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowPermissionError(false);
              onCancel();
            }}
            color="primary"
          >
            Stäng
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CameraCapture;
